// janus-engine/inventory/floor-move.ts — I1: the client-truth FLOOR → FLOOR inventory drag (Janus lane L2).
//
// PURE. Decides where a loose encounter row dragged to a new spot on the SAME encounter's floor comes to rest, and
// whether the browser may commit that move itself. Mirrors the legacy floor→floor branch of
// options/tools/inventory/ft3d/move_draggable_inventory_inc.php (owner 2026-09-16 gravity rule):
//   ft_inventory_rest_z()          includes/functions/janus/encounter/ft3d_inventory_gravity.php
//   ft_inventory_attachment()      (mounted / integrated / ceiling / wall / wall-piece)
//   ft3d_support_z_at_or_below()   includes/functions/janus/encounter/check_and_apply_falling_encounter_inventory.php
//   ft_falling_shape_support_floor() + ft3d_stair_support_at() + ft_shape_with_roof_decks()
//   ft_ground_occupancy_shape() / _height_ft() / ft_inventory_is_solid_obstacle()   ft_ground_occupancy.php
//   inventory_ground_move_weight_gate(_required)()   includes/functions/inventory/move_inventory_inc.php
// Oracle: tools/test/fixtures/i1-rest-z-oracle.json (tools/probes/i1_rest_z_oracle.php); unit test
// tools/test/janus-i1-floor-move.test.mjs. Coordinates are the FT3D contract: x/y = ground plane, z = FEET.
//
// Three outcomes, exactly as A1 / R1:
//   commit   — the envelope `after` + rest; the caller applies it optimistically and commits (publish-first).
//   refuse   — the legacy endpoint would refuse with this exact message; nothing is sent anywhere.
//   handBack — outside I1's boundary (fall damage, an overlay-dependent wall contact, unreadable data …); the caller
//              runs the legacy endpoint UNTOUCHED.

import { containingBoxes, floorAt, segmentHit } from '../../../../ft3d/engine/ft3d-shape-core.js';
import { roofDeckBoxes } from '../../encounter-creation-engine/floor-band.js';
import { dedicatedPropSpace } from './prop-mesh-space.js';
import { clearanceAboveFt, effectivePropScale, inventoryMeshSizeRatio, inventorySizeRatioEligible } from './mesh-scale.js';
export { inventorySolid, type SolidInputs } from './solid-obstacle.js';

type Row = Record<string, unknown>;

export const FLOOR_MOVE_SCHEMA = 'janus.inventory.floor_move.v1' as const;
/** How close counts as touching a wall / ceiling / wall piece (FT_INVENTORY_REST_CONTACT_FT). */
export const REST_CONTACT_FT = 0.5;
/** Resting-on tolerance (FT_INVENTORY_REST_EPS_FT). */
export const REST_EPS_FT = 0.05;
/** A fall this long damages a row with vitality — object damage stays on the legacy lane (R2). */
export const FALL_DAMAGE_THRESHOLD_FT = 5;
const METERS_PER_FOOT = 0.3048;
const OCCUPANCY_EPS = 1e-6;

// ---------------------------------------------------------------------------------------------------------------
// Occupancy zones (ft3d_move_core.php twins)
// ---------------------------------------------------------------------------------------------------------------
export type CircleZone = { kind: 'circle'; x: number; y: number; r: number; zMin: number; zMax: number };
export type OblongZone = { kind: 'oblong'; x: number; y: number; halfW: number; halfD: number; facing: number; zMin: number; zMax: number };
export type Zone = CircleZone | OblongZone;

const circle = (x: number, y: number, r: number, zMin: number, zMax: number): CircleZone =>
  ({ kind: 'circle', x, y, r: Math.max(0, r), zMin, zMax });
const oblong = (x: number, y: number, halfW: number, halfD: number, facing: number, zMin: number, zMax: number): OblongZone =>
  ({ kind: 'oblong', x, y, halfW: Math.max(0, halfW), halfD: Math.max(0, halfD), facing, zMin, zMax });

/** ft_move_to_local(): a world point into an oblong's local frame (facing along +local-x). */
export function toLocal(zone: OblongZone, px: number, py: number): [number, number] {
  const dx = px - zone.x, dy = py - zone.y;
  const c = Math.cos(-zone.facing), s = Math.sin(-zone.facing);
  return [dx * c - dy * s, dx * s + dy * c];
}

/** ft_inventory_footprint_extent_ft(): how far a footprint reaches from its centre along world direction `angle`. */
export function footprintExtentFt(zone: Zone | null, angle: number): number {
  if (!zone) return 1.25;
  if (zone.kind === 'oblong') {
    const rel = angle - zone.facing;
    return Math.max(0.25, zone.halfW * Math.abs(Math.cos(rel)) + zone.halfD * Math.abs(Math.sin(rel)));
  }
  return Math.max(0.25, zone.r);
}

/** ft_inventory_distance_to_zone_ft(): horizontal distance from (x, y) to a footprint's edge + the direction to it. */
export function distanceToZoneFt(zone: Zone, x: number, y: number): [number, number] {
  if (zone.kind === 'oblong') {
    const [lx, ly] = toLocal(zone, x, y);
    const cx = Math.max(-zone.halfW, Math.min(zone.halfW, lx));
    const cy = Math.max(-zone.halfD, Math.min(zone.halfD, ly));
    const ldx = cx - lx, ldy = cy - ly;
    const wx = ldx * Math.cos(zone.facing) - ldy * Math.sin(zone.facing);
    const wy = ldx * Math.sin(zone.facing) + ldy * Math.cos(zone.facing);
    return [Math.hypot(wx, wy), Math.atan2(wy, wx)];
  }
  const dx = zone.x - x, dy = zone.y - y;
  return [Math.max(0, Math.hypot(dx, dy) - zone.r), Math.atan2(dy, dx)];
}

function zoneHolds(zone: Zone, x: number, y: number): boolean {
  if (zone.kind === 'oblong') {
    const [lx, ly] = toLocal(zone, x, y);
    return !(Math.abs(lx) > zone.halfW + OCCUPANCY_EPS || Math.abs(ly) > zone.halfD + OCCUPANCY_EPS);
  }
  return !(Math.hypot(x - zone.x, y - zone.y) > zone.r + OCCUPANCY_EPS);
}

// ---------------------------------------------------------------------------------------------------------------
// Inventory footprint (ft_ground_occupancy_shape / _height_ft for an INVENTORY row, support_plane_z: true)
// ---------------------------------------------------------------------------------------------------------------
export interface InventoryTransform3d {
  schema?: string; scale?: number; scaleMode?: string; scaleXYZ?: number[]; rotationDegrees?: number; mounted?: boolean;
}

/** The inputs PHP's footprint reader uses, resolved by the caller from the page roster + catalog. */
export interface FootprintInputs {
  itemType: string;
  x: number; y: number; z: number;
  transform: InventoryTransform3d | null;
  /** The row's size id: `inventory_size` override, else the template's `size` layer (get_object_size_id_for_footprint). */
  sizeId: string | null;
  /** The row's own `inventory_size` override (drives the size ratio of a non-absolute mesh). */
  inventorySize: string | null;
  /** The template's `size` layer id. */
  templateSizeId: string | null;
  /** The selected world mesh's measured bounds [x, y(up), z] in metres (ft_inventory_world_mesh), or null. */
  worldMeshBoundsM: [number, number, number] | null;
  worldMeshAuthoredSizeTypeId?: string;
  /** The held scene/encounter shape used by the renderer's physical ceiling cap. */
  shape?: unknown;
}

/** get_size_dimensions(width) — SizeType base_size, Medium (5) when the id is unknown. */
function sizeBaseFt(sizeId: string | null, sizeBase: (id: string) => number | null): number {
  if (!sizeId) return 5;
  const base = sizeBase(sizeId);
  return base !== null && base > 0 ? base : 5;
}

const numeric = (v: unknown): v is number | string =>
  (typeof v === 'number' && Number.isFinite(v)) || (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)));
const round4 = (n: number): number => Math.round(n * 10000) / 10000;

/** ft_normalize_inventory_transform_3d(): a missing schema reads as v1; out-of-range scale voids the transform. */
export function normalizeTransform(raw: unknown): InventoryTransform3d | null {
  let value = raw;
  if (typeof value === 'string') { if (value.trim() === '') return null; try { value = JSON.parse(value); } catch { return null; } }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const t = value as Row;
  if ((t.schema ?? 'ft.inventory-transform-3d.v1') !== 'ft.inventory-transform-3d.v1') return null;
  const out: InventoryTransform3d = { schema: 'ft.inventory-transform-3d.v1' };
  let fields = 0;
  if (t.renderMode === 'standee') fields++;
  if (t.scaleMode === 'absolute') { out.scaleMode = 'absolute'; fields++; }
  if (t.mounted === true) { out.mounted = true; fields++; }
  if (t.rotationDegrees !== null && t.rotationDegrees !== '' && numeric(t.rotationDegrees)) {
    let rotation = Number(t.rotationDegrees) % 360;
    if (rotation < 0) rotation += 360;
    rotation = round4(rotation);
    if (Math.abs(rotation) >= 0.0001 && Math.abs(rotation - 360) >= 0.0001) { out.rotationDegrees = rotation; fields++; }
  }
  if (t.scale !== null && t.scale !== undefined && t.scale !== '' && numeric(t.scale)) {
    const scale = round4(Number(t.scale));
    if (!Number.isFinite(scale) || scale < 0.05 || scale > 24) return null;
    if (Math.abs(scale - 1) >= 0.0001) { out.scale = scale; fields++; }
  }
  if (Object.prototype.hasOwnProperty.call(t, 'scaleXYZ')) {
    const axes = t.scaleXYZ;
    if (!Array.isArray(axes) || axes.length !== 3) return null;
    const clean: number[] = [];
    for (const axis of axes) {
      if (!numeric(axis) || Number(axis) < 0.05 || Number(axis) > 24) return null;
      clean.push(round4(Number(axis)));
    }
    if (!(clean[0] === 1 && clean[1] === 1 && clean[2] === 1)) { out.scaleXYZ = clean; fields++; }
  }
  if (t.rubbleSource && typeof t.rubbleSource === 'object') fields++;
  return fields === 0 ? null : out;
}

/** The selected, drawn mesh's scaled dimensions in feet, independent of item type. */
export function scaledMeshBoundsFt(input: FootprintInputs, sizeBase: (id: string) => number | null): [number, number, number] | null {
  const bounds = input.worldMeshBoundsM;
  if (!bounds || bounds.length !== 3 || !bounds.every(v => Number.isFinite(v) && v > 0)) return null;
  const transform = input.transform;
  let ratio = 1;
  if (inventorySizeRatioEligible({ hasNativeMesh: true, isOverrideMesh: false, isRubbleFallback: false,
    absoluteTransform: transform?.scaleMode === 'absolute', itemType: input.itemType })) {
    ratio = inventoryMeshSizeRatio(input.inventorySize, input.templateSizeId, input.worldMeshAuthoredSizeTypeId, sizeBase);
  }
  const axes = transform?.scaleXYZ ?? [1, 1, 1];
  const scale = effectivePropScale(transform?.scale ?? 1, ratio, bounds[1] * axes[1]!,
    clearanceAboveFt(input.shape, input.x, input.y, input.z));
  return bounds.map((v, i) => v * axes[i]! * scale / METERS_PER_FOOT) as [number, number, number];
}

/** Mesh space for ordinary props; structural proxies and trees retain their dedicated readers. */
export function physicalMeshBoundsFt(input: FootprintInputs, sizeBase: (id: string) => number | null): [number, number, number] | null {
  return dedicatedPropSpace(input.itemType) ? null : scaledMeshBoundsFt(input, sizeBase);
}

/**
 * Inventory occupancy uses the selected mesh's scaled oblong, else the standee's size-tier cylinder.
 * Dedicated structural and tree readers retain their policies. Coordinates may override the row's position.
 */
export function inventoryFootprint(input: FootprintInputs, sizeBase: (id: string) => number | null,
  at: { x?: number; y?: number; z?: number } = {}): Zone {
  const px = at.x ?? input.x, py = at.y ?? input.y;
  const transform = input.transform;
  const facing = -((transform?.rotationDegrees ?? 0) * Math.PI / 180);
  const mesh = physicalMeshBoundsFt({ ...input, x: px, y: py, z: at.z ?? input.z }, sizeBase);
  const base = mesh ? Math.max(mesh[0], mesh[2]) : sizeBaseFt(input.sizeId, sizeBase);
  const height = mesh ? mesh[1] : base;
  const z = at.z ?? input.z;
  if (mesh) return oblong(px, py, mesh[0] / 2, mesh[2] / 2, facing, z, z + height);
  return circle(px, py, base / 2, z, z + height);
}

/** ft_ground_occupancy_height_ft() for an inventory row. */
export function inventoryHeightFt(input: FootprintInputs, sizeBase: (id: string) => number | null): number {
  const mesh = physicalMeshBoundsFt(input, sizeBase);
  if (mesh) return mesh[1];
  return sizeBaseFt(input.sizeId, sizeBase);
}

// ---------------------------------------------------------------------------------------------------------------
// The world a rest query reads
// ---------------------------------------------------------------------------------------------------------------
export interface ShapeBox {
  id?: string; x: { min: number; max: number }; y: { min: number; max: number }; floor: number; ceiling: number;
  creation?: Record<string, unknown> | null; navigationOpenGround?: boolean; roofDeck?: boolean;
}
export interface RestShape { boxes: ShapeBox[]; openings?: unknown[]; authoring?: Record<string, unknown> | null; sceneWalls?: unknown[]; [k: string]: unknown }

/** One encounter inventory row as the rest rule sees it. */
export interface RestRow {
  id: string;
  itemType: string;
  /** ft_inventory_is_solid_obstacle() — private rows, markers, roofs, bodies, loot and wall runs are not. */
  solid: boolean;
  /** Footprint at the row's own position, support plane (feet at z). */
  zone: Zone;
  /** z_pos (feet) and collision height. */
  z: number;
  height: number;
}

export interface RestWorld {
  /** ft_encounter_shape() — already overlaid with runtime roof state (the caller's job), or null when shapeless. */
  shape: RestShape | null;
  /** ft_encounter_id_is_depth_authored(): `encounter_shape IS NOT NULL`. */
  depthAuthored: boolean;
  rows: RestRow[];
}

/** The dragged row. */
export interface RestMover {
  id: string;
  mounted: boolean;
  integrated: boolean;
  /** Footprint recentred at a candidate (x, y); its vertical span stays the row's own (PHP passes no z). */
  footprintAt: (x: number, y: number) => Zone;
  height: number;
}

// ---------------------------------------------------------------------------------------------------------------
// Supports (stairs, roof decks, authored floors, solid props)
// ---------------------------------------------------------------------------------------------------------------
type StairGeometry = { points: [number, number, number][]; width: number };

/** ft3d_stair_geometry(): throws when the connection is not a walkable authored stair. */
export function stairGeometry(connection: Row, boxes: ShapeBox[]): StairGeometry {
  const fail = (message: string): never => { throw new Error(message); };
  if (connection.traversalEnabled !== true) fail('closed');
  const axes = (Array.isArray(connection.scaleXYZ) ? connection.scaleXYZ : [1, 1, 1]) as unknown[];
  if (axes.length !== 3 || axes.some(n => typeof n !== 'number' || !Number.isFinite(n) || n < .05 || n > 24)) fail('scale');
  const [ax, , az] = axes as number[];
  const rooms = new Map(boxes.map(box => [String(box.id ?? ''), box]));
  const source = rooms.get(String(connection.sourceRoomId ?? ''));
  const target = rooms.get(String(connection.targetRoomId ?? ''));
  const geometry = (connection.asset as Row | undefined)?.geometry as Row | undefined;
  const route = (Array.isArray(geometry?.routes) ? geometry!.routes[0] : null) as Row | null;
  if (!source || !target || !route || !route.walkable || route.kind !== 'stair') fail('geometry');
  const anchor = connection.anchorFt as { x: number; y: number; z: number };
  const q = Math.trunc(Number(connection.rotationQuarterTurns ?? 0));
  if (q < 0 || q > 3 || Math.abs(anchor.z - source!.floor) > .01) fail('anchor');
  const points: [number, number, number][] = [];
  for (const point of (route!.centerlineM as unknown[][])) {
    const [x0, h, y0] = point.map((n, i) => Number(n) * (axes as number[])[i]! / METERS_PER_FOOT) as [number, number, number];
    const [x, y] = ([[x0, y0], [y0, -x0], [-x0, -y0], [-y0, x0]] as [number, number][])[q]!;
    points.push([x + anchor.x, y + anchor.y, h + anchor.z]);
  }
  if (points.length !== 2 || Math.abs(points[0]![2] - source!.floor) > .01 || Math.abs(points[1]![2] - target!.floor) > .01) fail('landings');
  const width = Number(route!.usableWidthM) * Math.min(ax!, az!) / METERS_PER_FOOT;
  const dx = points[1]![0] - points[0]![0], dy = points[1]![1] - points[0]![1], run = Math.hypot(dx, dy);
  if (run < 1) fail('run');
  const ux = dx / run, uy = dy / run, landing = 2.65;
  points.unshift([points[0]![0] - ux * landing, points[0]![1] - uy * landing, source!.floor]);
  const upper = points[2]!;
  points.push([upper[0] + ux * landing, upper[1] + uy * landing, target!.floor]);
  points.forEach((point, i) => {
    const room = i === 3 ? target! : source!;
    if (point[0] < room.x.min || point[0] > room.x.max || point[1] < room.y.min || point[1] > room.y.max) fail('outside');
  });
  return { points, width };
}

/** ft3d_stair_support_at(): the highest authored stair tread at or below the feet under (x, y). */
export function stairSupportAt(shape: RestShape, x: number, y: number, feet: number): number | null {
  let best: number | null = null;
  const connections = (shape.authoring as Row | null | undefined)?.verticalConnections;
  for (const connection of Array.isArray(connections) ? connections as Row[] : []) {
    let stair: StairGeometry;
    try { stair = stairGeometry(connection, shape.boxes ?? []); } catch { continue; }
    const a = stair.points[1]!, b = stair.points[2]!;
    const dx = b[0] - a[0], dy = b[1] - a[1], len2 = dx * dx + dy * dy;
    const t = ((x - a[0]) * dx + (y - a[1]) * dy) / len2;
    if (t < 0 || t > 1 || Math.hypot(x - (a[0] + t * dx), y - (a[1] + t * dy)) > stair.width / 2) continue;
    const z = a[2] + t * (b[2] - a[2]);
    if (z <= feet + .05 && (best === null || z > best)) best = z;
  }
  return best;
}

/** ft_shape_with_roof_decks(): the shape plus exactly the roof decks at or below $feet_z. */
export function withRoofDecks(shape: RestShape, feetZ: number): RestShape {
  const boxes = (shape.boxes ?? []).filter(box => !(box.roofDeck && box.floor > feetZ + .05));
  const present = new Set(boxes.filter(box => box.roofDeck).map(box => String(box.id ?? '')));
  // PHP's deck boxes are query-only OPEN GROUND (`navigationOpenGround` — ft3d_floor_band.php): they contribute no faces, so two
  // adjacent roofs are one walkable surface and a structural room at the same level outranks a deck in floorAt / ceilingAt. The shared
  // roofDeckBoxes() (floor-band.ts) omits the flag for its scene consumers; the Janus twin adds it (Force Move S3 oracle: a body on the
  // keyhole cell's roofs read the deck seam as a wall).
  const decks = (roofDeckBoxes({ ...shape, boxes } as never, feetZ) as unknown as ShapeBox[]).map(deck => ({ ...deck, navigationOpenGround: true }));
  return { ...shape, boxes: [...boxes, ...decks.filter(deck => !present.has(String(deck.id ?? '')))] };
}

/** ft_falling_shape_support_floor(): the highest authored floor / stair tread beneath the feet. */
export function fallingShapeSupportFloor(shape: RestShape, x: number, y: number, feetZ: number, graceFt: number): number | null {
  let highest = stairSupportAt(shape, x, y, feetZ);
  let grace = graceFt;
  if (highest !== null) grace = .05;
  const decked = withRoofDecks(shape, feetZ);
  for (const box of containingBoxes(decked as never, x, y) as ShapeBox[]) {
    const floor = Number(box.floor);
    if (floor > feetZ + grace) continue;
    if (highest === null || floor > highest) highest = floor;
  }
  return highest;
}

/** ft3d_support_z_at_or_below(): the highest surface at or below the feet (+eps) under (x, y); null when none. */
export function supportZAtOrBelow(world: RestWorld, x: number, y: number, feetZ: number, selfId: string | null, contactEps = REST_EPS_FT): number | null {
  let landing: number | null = world.shape ? fallingShapeSupportFloor(world.shape, x, y, feetZ, contactEps) : null;
  const self = selfId ? selfId.toUpperCase() : null;
  for (const row of world.rows) {
    if ((self !== null && row.id.toUpperCase() === self) || !row.solid) continue;
    if (!zoneHolds(row.zone, x, y)) continue;
    const top = row.z + row.height;
    if (top <= feetZ + contactEps && (landing === null || top > landing)) landing = top;
  }
  return landing;
}

// ---------------------------------------------------------------------------------------------------------------
// Attachment + rest
// ---------------------------------------------------------------------------------------------------------------
export type RestReason = 'placed' | 'fell' | 'mounted' | 'integrated' | 'wall' | 'ceiling' | 'wall-piece' | 'unsupported';
export type Attachment = 'mounted' | 'integrated' | 'wall' | 'ceiling' | 'wall-piece';

export interface AttachmentProbe {
  /** Set when the answer read an authored WALL ray (runtime door / breach / curtain overlays can change those). */
  wallRayConsulted: boolean;
}

/** ft_inventory_attachment(): why the row stays where it is placed, or null when nothing holds it up. */
export function inventoryAttachment(world: RestWorld, mover: RestMover, x: number, y: number, z: number,
  contactAttaches = true, probe: AttachmentProbe = { wallRayConsulted: false }): Attachment | null {
  if (mover.mounted) return 'mounted';
  if (mover.integrated) return 'integrated';
  if (!contactAttaches) return null;
  const footprint = mover.footprintAt(x, y);
  const height = mover.height;
  const shape = world.shape;
  if (shape) {
    for (const box of containingBoxes(shape as never, x, y) as ShapeBox[]) {
      const floor = Number(box.floor), ceiling = Number(box.ceiling);
      if (z >= floor - REST_EPS_FT && z < ceiling && (z + height) >= ceiling - REST_CONTACT_FT && boxRoofed(box)) return 'ceiling';
    }
    const probeZ = z + Math.max(0.1, Math.min(height, 5.0) / 2);
    probe.wallRayConsulted = true;
    for (let i = 0; i < 8; i++) {
      const angle = i * Math.PI / 4;
      const reach = footprintExtentFt(footprint, angle) + REST_CONTACT_FT;
      const hit = segmentHit(shape as never, x, y, x + Math.cos(angle) * reach, y + Math.sin(angle) * reach, { z: probeZ, mode: 'sight', containToUnion: false });
      if (hit !== null) return 'wall';
    }
  }
  const self = mover.id.toUpperCase();
  for (const other of world.rows) {
    if (other.id.toUpperCase() === self) continue;
    if (!['wall', 'door', 'window'].includes(other.itemType) || !other.solid) continue;
    const otherTop = other.z + other.height;
    if (z > otherTop || z + height < other.z) continue;
    const [gap, toward] = distanceToZoneFt(other.zone, x, y);
    if (gap - footprintExtentFt(footprint, toward) <= REST_CONTACT_FT) return 'wall-piece';
  }
  return null;
}

/** ft_shape_box_is_roofed(). */
function boxRoofed(box: ShapeBox): boolean {
  const creation = box.creation ?? {};
  return String(creation.enclosure ?? '').includes('ceiling') && creation.roofRemoved !== true;
}

export interface RestResult { z: number; rest: RestReason; landing: number | null; wallRayConsulted: boolean }

/** PHP round($n, 3): half away from zero (Math.round rounds half toward +∞ — a negative support differed). */
export const round3 = (n: number): number => Math.sign(n) * Math.round(Math.abs(n) * 1000) / 1000;

/** ft_inventory_rest_z(): the feet elevation a placed row comes to rest at, and why. */
export function inventoryRestZ(world: RestWorld, mover: RestMover, x: number, y: number, z: number, contactAttaches = true): RestResult {
  let landing = supportZAtOrBelow(world, x, y, z, mover.id || null, REST_EPS_FT);
  const shape = world.shape;
  const outsideBoxes = !!shape && floorAt(shape as never, x, y) === null;
  if (z >= 0 && (!shape || outsideBoxes || !world.depthAuthored)) landing = Math.max(landing ?? 0, 0);
  if (landing === null) return { z, rest: 'unsupported', landing, wallRayConsulted: false };
  if (z <= landing + REST_EPS_FT) return { z, rest: 'placed', landing, wallRayConsulted: false };
  const probe: AttachmentProbe = { wallRayConsulted: false };
  const attachment = inventoryAttachment(world, mover, x, y, z, contactAttaches, probe);
  if (attachment !== null) return { z, rest: attachment, landing, wallRayConsulted: probe.wallRayConsulted };
  return { z: round3(landing), rest: 'fell', landing, wallRayConsulted: probe.wallRayConsulted };
}

// ---------------------------------------------------------------------------------------------------------------
// The Edit Scene height gestures (options/api/ft3d_inventory_rotation_inc.php :192-244)
// ---------------------------------------------------------------------------------------------------------------
export type HeightGesture = 'height' | 'drop';
export interface GestureResult extends RestResult { mounted: boolean; releaseZ: number }

/**
 * `height` (the Z arrows): judged WITHOUT the pin — if nothing (support, wall, ceiling) holds it there the arrows meant it,
 * so pin it; if it rests on its support, unpin; otherwise keep the pin as it was. `drop` (the ground control): unpin and
 * fall from the higher of the requested and the stored height, and a dropped object does not stick to a wall.
 */
export function resolveHeightGesture(world: RestWorld, mover: RestMover, gesture: HeightGesture,
  x: number, y: number, z: number, storedZ: number): GestureResult {
  let mounted = mover.mounted;
  let probeReadWall = false;
  if (gesture === 'height') {
    const probe = inventoryRestZ(world, { ...mover, mounted: false }, x, y, z, true);
    probeReadWall = probe.wallRayConsulted;
    if (probe.rest === 'fell') mounted = true;
    else if (probe.rest === 'placed') mounted = false;
  } else {
    mounted = false;
  }
  const releaseZ = gesture === 'drop' ? Math.max(z, storedZ) : z;
  const rest = inventoryRestZ(world, { ...mover, mounted }, x, y, releaseZ, gesture !== 'drop');
  // The pin decision read the probe's wall ray too: an overlay-bearing shape must hand either read back.
  return { ...rest, wallRayConsulted: rest.wallRayConsulted || probeReadWall, mounted, releaseZ };
}

// ---------------------------------------------------------------------------------------------------------------
// Weight gate
// ---------------------------------------------------------------------------------------------------------------
/** inventory_ground_move_weight_gate_required(). */
export function weightGateRequired(sourcePossessorType: string, hasElevatedAuthority: boolean,
  explicitEditorSurface: boolean, hasExplicitCaster: boolean): boolean {
  if (sourcePossessorType !== 'encounter' || explicitEditorSurface) return false;
  return !hasElevatedAuthority || hasExplicitCaster;
}

export interface WeightGateInputs {
  /** computed.weight.gross ?? computed weight total (whole stack), or null. */
  grossStackLb: number | null;
  /** The `weight` layer value (per unit), or null. */
  layerWeightLb: number | null;
  itemQty: number;
  /** caster computed capacity push_drag_lift total ?? creature_push_drag_lift_capacity layer; null when unreadable. */
  capacityRaw: unknown;
}
export interface WeightGateResult { allowed: boolean; movedLb: number; capacityLb: number | null; reason: '' | 'too_heavy' | 'capacity_unavailable' }

/** inventory_ground_move_weight_gate(). */
export function weightGate(input: WeightGateInputs, qty: number): WeightGateResult {
  const stackQty = Math.max(1, Math.trunc(input.itemQty || 1));
  let gross = Number(input.grossStackLb ?? 0) || 0;
  if (gross <= 0) gross = (Number(input.layerWeightLb ?? 0) || 0) * stackQty;
  const movedLb = (gross / stackQty) * Math.max(1, qty);
  const raw = input.capacityRaw;
  const numeric = typeof raw === 'number' || (typeof raw === 'string' && raw.trim() !== '' && Number.isFinite(Number(raw)));
  if (raw === null || raw === undefined || raw === '' || !numeric) return { allowed: false, movedLb, capacityLb: null, reason: 'capacity_unavailable' };
  const capacityLb = Math.max(0, Number(raw));
  const allowed = movedLb <= capacityLb + 0.0001;
  return { allowed, movedLb, capacityLb, reason: allowed ? '' : 'too_heavy' };
}

/** make_number_pretty(): thousands separators; two decimals when there is a fractional part. */
export function pretty(n: number): string {
  const fractional = Math.abs(n - Math.round(n)) > 0.000001;
  const fixed = fractional ? n.toFixed(2) : String(Math.trunc(n));
  const [int, dec] = fixed.split(".");
  const sign = int!.startsWith("-") ? "-" : "";
  const digits = sign ? int!.slice(1) : int!;
  return sign + digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (dec !== undefined ? "." + dec : "");
}

// ---------------------------------------------------------------------------------------------------------------
// The drag resolver
// ---------------------------------------------------------------------------------------------------------------
export interface FloorMoveInput {
  encounterId: string;
  inventoryId: string;
  /** The row as the page holds it (possessor, deletion, quantity, position). */
  row: { possessId: string; deleted: boolean; qty: number; x: number; y: number; z: number; transformSha: string };
  /** The scene's requested destination (ground pair + feet). */
  to: { x: number; y: number; z: number };
  /** Player-side legality the legacy endpoint enforces for non-GM / non-editor viewers. */
  elevated: boolean;
  explicitEditorSurface: boolean;
  actorId: string | null;
  immovable: boolean;
  itemType: string;
  weight: WeightGateInputs | null;
  /** The encounter's shape has runtime overlays the page cannot reproduce (openings / wall runs / curtains). */
  shapeOverlayUnknown: boolean;
  /** The row has vitality (fall damage would apply). Null = unknown. */
  hasVitality: boolean | null;
  /** An Edit Scene height gesture (Z arrows / ground control); absent for a plain planar placement. */
  gesture?: HeightGesture | null;
  /** The Place-object / Edit Scene writer moves the WHOLE row (a stack travels together, as the transform endpoint did).
   * False = the retired scene-drop hook, which posted item_qty 1 (a split — a new row identity, C1k's). */
  wholeRow?: boolean;
}

export type FloorMoveVerdict =
  | { verdict: 'commit'; after: { x: number; y: number; z: number }; rest: RestReason; fallFt: number; landing: number | null; mounted?: boolean }
  | { verdict: 'refuse'; reason: string; message: string }
  | { verdict: 'handBack'; reason: string };

/** The floor→floor drag: legality (immovable / digsite / weight) → gravity → the commit verdict. */
export function resolveFloorMove(input: FloorMoveInput, world: RestWorld, mover: RestMover): FloorMoveVerdict {
  const enc = input.encounterId.toUpperCase();
  if (input.row.deleted || input.row.possessId.toUpperCase() !== enc) return { verdict: 'handBack', reason: 'not-a-floor-row' };
  if (!Number.isSafeInteger(input.row.qty) || input.row.qty < 1) return { verdict: 'handBack', reason: 'qty' };
  // The retired scene-drop hook posted item_qty 1: on a stack that is a SPLIT (a new row identity — C1k's). The Place-object
  // / Edit Scene writer moves the whole row, stack included.
  if (!input.wholeRow && input.row.qty !== 1) return { verdict: 'handBack', reason: 'stack' };
  const movedQty = input.wholeRow ? input.row.qty : 1;
  if (![input.to.x, input.to.y, input.to.z].every(n => Number.isFinite(n) && Math.abs(n) < 100000)) return { verdict: 'handBack', reason: 'destination' };
  // The physical weight rule (a GM arranging the scene without a caster, or the editor surface, bypasses it).
  if (weightGateRequired('encounter', input.elevated, input.explicitEditorSurface, !!input.actorId)) {
    if (!input.weight) return { verdict: 'handBack', reason: 'weight-unreadable' };
    const gate = weightGate(input.weight, movedQty);
    if (gate.reason === 'capacity_unavailable') return { verdict: 'handBack', reason: 'capacity-unreadable' };
    if (!gate.allowed) {
      return { verdict: 'refuse', reason: 'too-heavy', message: `That object weighs ${pretty(gate.movedLb)} lb; your push, drag, or lift limit is ${pretty(gate.capacityLb ?? 0)} lb.` };
    }
  }
  if (!input.elevated) {
    if (!input.actorId) return { verdict: 'handBack', reason: 'no-actor' };
    if (input.immovable) return { verdict: 'refuse', reason: 'immovable', message: 'That object is fixed in place and cannot be moved.' };
    if (input.itemType === 'digsite') return { verdict: 'refuse', reason: 'digsite', message: 'The digsite is part of the terrain — it cannot be moved, only excavated.' };
  }
  const gesture = input.gesture ?? null;
  const rest: RestResult & { mounted?: boolean; releaseZ?: number } = gesture
    ? resolveHeightGesture(world, mover, gesture, input.to.x, input.to.y, input.to.z, input.row.z)
    : inventoryRestZ(world, mover, input.to.x, input.to.y, input.to.z, true);
  if (rest.wallRayConsulted && input.shapeOverlayUnknown) return { verdict: 'handBack', reason: 'shape-overlay' };
  const releaseZ = rest.releaseZ ?? input.to.z;
  const fallFt = rest.rest === 'fell' ? Math.max(0, releaseZ - rest.z) : 0;
  // A fall that would damage the object (> 5 ft) commits too: the adapter resolves the damage (object-fall-damage.ts, H3).
  return { verdict: 'commit', after: { x: input.to.x, y: input.to.y, z: rest.z }, rest: rest.rest, fallFt, landing: rest.landing,
    ...(gesture ? { mounted: rest.mounted === true } : {}) };
}
